import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "@/user/UserAuthContext";
import { LOGO_URL } from "@/lib/useReveal";

const NAV = [
    { to: "dashboard", label: "Overview", icon: iconGrid },
    { to: "company", label: "Company Profile", icon: iconBuilding },
    { to: "employees", label: "Employees", icon: iconUsers },
    { to: "invitations", label: "Invitations", icon: iconMail },
    { to: "departments", label: "Departments", icon: iconLayers },
    { to: "roles", label: "Roles", icon: iconShield },
    { to: "permissions", label: "Permissions", icon: iconKey },
    { to: "settings", label: "Settings", icon: iconGear },
    { to: "profile", label: "Profile", icon: iconUser },
];

export default function CompanyAdminLayout() {
    const { user, company, status, logout } = useUserAuth();
    const location = useLocation();
    const nav = useNavigate();

    if (status === "loading") {
        return <div className="dot-grid min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
    }
    if (status === "guest" || !user) {
        return <Navigate to="/company-admin/login" state={{ from: location }} replace />;
    }
    if (user.role !== "company_admin") {
        return (
            <div className="dot-grid min-h-screen flex items-center justify-center px-6">
                <div className="glass-strong rounded-3xl p-8 max-w-lg text-center">
                    <div className="font-display text-2xl text-black">Access denied</div>
                    <p className="mt-2 text-[13.5px] text-neutral-600">
                        Your account is signed in as <b>{user.role}</b>, not <b>company_admin</b>. This portal is for
                        company admins only.
                    </p>
                    <div className="mt-5">
                        <button
                            onClick={async () => { await logout(); nav("/company-admin/login", { replace: true }); }}
                            className="inline-flex items-center gap-1.5 rounded-full bg-black text-white text-sm font-medium px-5 py-2.5"
                        >
                            Switch account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (location.pathname === "/company-admin" || location.pathname === "/company-admin/") {
        return <Navigate to="/company-admin/dashboard" replace />;
    }

    async function onLogout() {
        await logout();
        nav("/company-admin/login", { replace: true });
    }

    const current = NAV.find((n) => location.pathname === `/company-admin/${n.to}`) || NAV[0];

    return (
        <div className="dot-grid min-h-screen w-full flex text-black">
            <aside
                data-testid="ca-sidebar"
                className="hidden lg:flex flex-col w-[260px] shrink-0 sticky top-0 h-screen px-5 py-6 border-r border-black/[0.06] bg-white/70 backdrop-blur-xl"
            >
                <div className="flex items-center gap-2.5 px-1.5">
                    <img src={LOGO_URL} alt="" className="logo-blend h-9 w-9 object-contain" />
                    <div>
                        <div className="font-display text-[15.5px] font-medium leading-none">
                            {company?.name || "Company"}
                        </div>
                        <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-violet-600 mt-1">
                            Admin Portal
                        </div>
                    </div>
                </div>

                <nav className="mt-8 flex-1 space-y-0.5">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`ca-sidebar-${n.to}`}
                            className={({ isActive }) =>
                                `group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] transition-all ${
                                    isActive
                                        ? "bg-black text-white shadow-[0_10px_30px_-15px_rgba(139,92,246,0.55)]"
                                        : "text-neutral-700 hover:bg-black/[0.04]"
                                }`
                            }
                        >
                            <span className="inline-flex items-center justify-center h-6 w-6">{n.icon()}</span>
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <button
                    onClick={onLogout}
                    data-testid="ca-sidebar-logout"
                    className="mt-6 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] text-rose-600 hover:bg-rose-50 transition-colors"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M15 12H3M8 7L3 12l5 5M17 3h2a2 2 0 012 2v14a2 2 0 01-2 2h-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Logout
                </button>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                <header data-testid="ca-topbar" className="sticky top-0 z-30 flex items-center justify-between gap-4 px-6 lg:px-10 h-16 border-b border-black/[0.06] bg-white/75 backdrop-blur-xl">
                    <div className="flex items-center gap-3">
                        <span className="lg:hidden inline-flex items-center gap-2">
                            <img src={LOGO_URL} alt="" className="logo-blend h-7 w-7 object-contain" />
                        </span>
                        <span className="hidden lg:block text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                            {current.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-2 text-[12px] text-neutral-500" data-testid="ca-topbar-company">
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 anim-pulse-glow"></span>
                            {company?.name} · <span className="text-violet-600">Company Admin</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-white border border-black/10 px-2 py-1.5 pr-3">
                            <span className="h-6 w-6 rounded-full bg-black text-white text-[11px] font-semibold inline-flex items-center justify-center">
                                {(user.name || "?").slice(0, 1).toUpperCase()}
                            </span>
                            <div className="leading-tight">
                                <div className="text-[12px] font-semibold text-black" data-testid="ca-profile-name">{user.name}</div>
                                <div className="text-[10px] text-neutral-500">{user.email}</div>
                            </div>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-6 lg:px-10 py-8 lg:py-10">
                    <Outlet context={{ user, company }} />
                </main>
            </div>
        </div>
    );
}

/* Icons */
function iconGrid() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/></svg>; }
function iconBuilding() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 21V5a2 2 0 012-2h6a2 2 0 012 2v16M14 21V11a2 2 0 012-2h2a2 2 0 012 2v10M2 21h20M8 7h2M8 11h2M8 15h2M16 13h2M16 17h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>; }
function iconUsers() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 20c1.2-3.5 4-5.5 7-5.5s5.8 2 7 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M15 20c.5-2.4 2.3-4 4-4s3.5 1.6 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>; }
function iconMail() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function iconLayers() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>; }
function iconShield() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>; }
function iconKey() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="8" cy="15" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M11 12l9-9M17 6l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>; }
function iconGear() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M19 12a7 7 0 00-.1-1.2l2.1-1.6-2-3.4-2.5.8a7 7 0 00-2-1.2L14 3h-4l-.5 2.4c-.7.3-1.4.7-2 1.2L5 5.8l-2 3.4L5.1 10.8A7 7 0 005 12c0 .4 0 .8.1 1.2L3 14.8l2 3.4 2.5-.8c.6.5 1.3.9 2 1.2L10 21h4l.5-2.4c.7-.3 1.4-.7 2-1.2l2.5.8 2-3.4-2.1-1.6c.1-.4.1-.8.1-1.2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>; }
function iconUser() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5"/><path d="M4 20c1.5-4 5-6 8-6s6.5 2 8 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>; }
